import { useState } from 'react'

import { PayrollSettingsPageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import PayrollConcepts from 'modules/compensations/pages/settings/Content/PayrollConcepts'
import PrimoboxActivationRow from 'modules/integrations_primobox/components/ActivationRow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import canSeeIntegration from 'modules/marketplace/lib/canSeeIntegration'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { usePayrollBundleSettings } from 'modules/payroll_bundle/hooks/usePayrollBundleSettings'
import PayrollPaywallGuard from 'modules/payroll_bundle/pages/PayrollPaywallGuard'
import ConnectedSupplements from 'modules/payroll_bundle/pages/settings/Content/ConnectedSupplements'
import EmployeeUpdates from 'modules/payroll_bundle/pages/settings/Content/EmployeeUpdates'
import EmployeesData from 'modules/payroll_bundle/pages/settings/Content/EmployeesData'
import Folders from 'modules/payroll_bundle/pages/settings/Content/Folders'
import Integrations from 'modules/payroll_bundle/pages/settings/Content/Integrations'
import PayrollPolicies from 'modules/payroll_bundle/pages/settings/Content/PayrollPolicies'
import ProfessionalSettings from 'modules/payroll_bundle/pages/settings/Content/ProfessionalSettings'
import TypesOfCompensation from 'modules/payroll_bundle/pages/settings/Content/TypesOfCompensation'

const PayrollBundleSettings = () => {
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const [isPrimoboxEnabled, setPrimoboxEnabled] = useState(false)

  const { loading: loadingCollections } = useAsyncData(() => companyInstallations.fetch(), [])

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const today = moment().format('YYYY-MM-DD')

  const result = useQuery(PayrollSettingsPageDocument, {
    variables: { companyId, today },
  })

  const features = useUnsafeMemo(
    () => result.data?.features?.featuresConnection.nodes || [],
    [result]
  )

  const hasFeature = useUnsafeCallback(
    (featureName: string) =>
      features.find((feature) => feature.name === featureName)?.enabled ?? false,
    [features]
  )

  useUnsafeEffect(() => {
    setPrimoboxEnabled(hasFeature('integrations_primobox_active'))
  }, [hasFeature, features])

  const { subscriptionAddon, isNotFound, loading: loadingSettings } = usePayrollBundleSettings()
  const { hasInhousePayroll } = useInhousePayroll()
  const hasProfessionals = hasFeature('factorial_professionals')

  const hasIntegrationInstalled =
    canSeeIntegration(integrationCodes.a3innuva, companyInstallations) ||
    canSeeIntegration(integrationCodes.a3nom, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev, companyInstallations) ||
    canSeeIntegration(integrationCodes.zucchetti, companyInstallations) ||
    canSeeIntegration(integrationCodes.paierh, companyInstallations) ||
    canSeeIntegration(integrationCodes.yeap_paierh, companyInstallations) ||
    canSeeIntegration(integrationCodes.silae, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev_api, companyInstallations) ||
    canSeeIntegration(integrationCodes.datev_lug_api, companyInstallations) ||
    canSeeIntegration(integrationCodes.gispaghe, companyInstallations)

  const policyEmployeeCounts = useUnsafeMemo(() => {
    const employees = result.data?.contracts?.payrollPolicyEmployees || []
    // Group employees by policy ID and count unique employee IDs
    const counts = new Map<number, Set<number>>()

    employees.forEach((employee: { payrollPolicyId?: number; employeeId?: number }) => {
      if (employee?.payrollPolicyId && employee?.employeeId) {
        const policyId = employee.payrollPolicyId
        if (!counts.has(policyId)) {
          counts.set(policyId, new Set())
        }
        counts.get(policyId)?.add(employee.employeeId)
      }
    })

    const resultMap = new Map<number, number>()
    counts.forEach((employeeIds, policyId) => {
      resultMap.set(policyId, employeeIds.size)
    })

    return resultMap
  }, [result.data])

  const showLoader = loadingSettings || loadingCollections || result.loading

  const authorized = !isNotFound && !!subscriptionAddon

  const content = (
    <>
      <PayrollPolicies policyEmployeeCounts={policyEmployeeCounts} />
      {hasProfessionals && <ProfessionalSettings />}
      {(hasInhousePayroll || hasProfessionals) && <EmployeeUpdates />}
      <TypesOfCompensation />
      <PayrollConcepts legacyPayroll />
      <ConnectedSupplements />
      {hasIntegrationInstalled && <Integrations installedIntegrations={companyInstallations} />}
      <EmployeesData />
      {!!subscriptionAddon && <Folders addon={subscriptionAddon} />}
      {hasFeature('integrations_primobox_activable') && (
        <PrimoboxActivationRow enabled={isPrimoboxEnabled} onToggle={setPrimoboxEnabled} />
      )}
    </>
  )

  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        loading={showLoader}
        error={null}
        authorized={authorized}
        showHeader={false}
      >
        {content}
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default PayrollBundleSettings
